<template>
  <section class="flex flex-col gap-2" data-cy="job-cost-header-panel">
    <div class="flex items-center justify-between gap-2 font-bold">
      <span>Job Cost</span>
      <span :class="marginClass" data-cy="job-cost-header-margin">
        <JobCostValue
          :value="jobCost.margin_pct"
          format="percent"
          :not-available="timesInvalid"
        />
      </span>
    </div>

    <dl class="m-0 flex flex-col gap-1 tabular-nums">
      <div
        v-for="row in rows"
        :key="row.key"
        class="flex justify-between gap-4"
        :class="row.strong ? 'font-bold text-gray-900' : 'text-gray-600'"
        :data-cy="`job-cost-header-row-${row.key}`"
      >
        <dt class="font-[inherit]">
          <template v-if="row.fullLabel">
            <Tooltip placement="top">
              <template #trigger>
                <abbr
                  class="cursor-help no-underline"
                  aria-hidden="true"
                  data-cy="job-cost-header-abbr"
                  >{{ row.label }}</abbr
                >
              </template>
              <template #tooltip>
                <span data-cy="job-cost-header-abbr-tooltip">{{
                  row.fullLabel
                }}</span>
              </template>
            </Tooltip>
            <span class="sr-only">{{ row.fullLabel }}</span>
          </template>
          <template v-else>{{ row.label }}</template>
        </dt>
        <dd
          class="m-0 text-right text-[length:inherit]"
          data-cy="job-cost-header-value"
        >
          <JobCostValue
            :value="row.value"
            :cost="row.cost"
            :not-available="timesInvalid"
          />
        </dd>
      </div>
      <div
        class="flex justify-between gap-4 font-bold"
        :class="signClass(jobCost.job_profit_cents)"
        data-cy="job-cost-header-row-profit"
      >
        <dt class="font-[inherit]">Profit</dt>
        <dd
          class="m-0 text-right text-[length:inherit]"
          data-cy="job-cost-header-value"
        >
          <JobCostValue
            :value="jobCost.job_profit_cents"
            :not-available="timesInvalid"
          />
        </dd>
      </div>
    </dl>

    <div
      v-if="stateMarker || jobCost.estimated"
      class="flex flex-wrap items-center gap-2"
    >
      <Badge
        v-if="stateMarker"
        variant="chip"
        :color="stateMarker.color"
        data-cy="job-cost-header-state"
        :text="stateMarker.label"
        :tooltip-text="stateMarker.tooltip"
      />
      <Badge
        v-if="jobCost.estimated"
        variant="chip"
        :color="ESTIMATED_MARKER.color"
        data-cy="job-cost-header-estimated"
        :text="ESTIMATED_MARKER.label"
        :tooltip-text="ESTIMATED_TOOLTIP"
      />
    </div>

    <button
      type="button"
      class="self-start border-0 bg-transparent p-0 text-left text-blue-600 hover:underline"
      data-cy="job-cost-view-breakdown"
      @click="viewBreakdown"
    >
      View Breakdown <span aria-hidden="true">→</span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { EventBus } from "@/lib/event_bus.js";

import Badge from "@/components/Badge.vue";
import Tooltip from "@/components/Tooltip.vue";
import { ESTIMATED_MARKER, STATE_MARKERS } from "@/Job/jobCostMarkers";
import JobCostValue from "@/Job/Tabs/JobCostValue.vue";
import type { JobCostSummary } from "@/typings/jobCost";

// The job header's compact view of the job_cost node. The node's state and
// masking come from the server; this component only renders them.
const props = defineProps<{ jobCost: JobCostSummary }>();

const LOSS_CLASS = "text-red-600";
const PROFIT_CLASS = "text-green-700";
const ESTIMATED_TOOLTIP =
  "Some costs are estimated. View Breakdown for details.";

interface PanelRow {
  key: string;
  label: string;
  fullLabel?: string;
  value: number | null;
  cost: boolean;
  strong?: boolean;
}

const timesInvalid = computed(() => props.jobCost.state === "times_invalid");
const stateMarker = computed(() => STATE_MARKERS[props.jobCost.state]);
const marginClass = computed(() =>
  (props.jobCost.margin_pct ?? 0) < 0 ? LOSS_CLASS : ""
);
const rows = computed<PanelRow[]>(() => [
  {
    key: "revenue",
    label: "Revenue",
    value: props.jobCost.revenue_cents,
    cost: false
  },
  {
    key: "labor",
    label: "Labor",
    value: props.jobCost.labor_cost_cents,
    cost: true
  },
  {
    key: "parts",
    label: "Parts",
    value: props.jobCost.parts_cost_cents,
    cost: true
  },
  {
    key: "finance_fee",
    label: "Fee",
    fullLabel: "Finance fee",
    value: props.jobCost.finance_fee_cents,
    cost: true
  },
  // Shown only on a job that has purchase orders, as on the tab.
  ...(props.jobCost.purchase_orders_cost_cents === 0
    ? []
    : [
        {
          key: "purchase_orders",
          label: "POs",
          fullLabel: "Purchase orders",
          value: props.jobCost.purchase_orders_cost_cents,
          cost: true
        }
      ]),
  {
    key: "cost",
    label: "COGS",
    value: props.jobCost.job_cost_cents,
    cost: true,
    strong: true
  }
]);

// Unlike the tab, zero profit reads green here.
function signClass(value: number | null): string {
  if (value === null) return "";
  return value < 0 ? LOSS_CLASS : PROFIT_CLASS;
}

function viewBreakdown(): void {
  EventBus.$emit("set-selected-tab", "Job Cost");
}
</script>
